<template>
  <el-dialog
    class="dialog-inner-scroller"
    :model-value="visible"
    :before-close="() => {
      emit('close');
    }"
    width="600"
  >
    <template #header>
      <div class="my-header">
        <h4 class="px-2 pb-3 text-xl text-[#344767] font-semibold">
          账号提现({{ data.serverName }}-支付宝记账簿提现)
        </h4>
      </div>
    </template>

    <div class="px-2 pb-3">
      <el-form
        ref="formRef"
        require-asterisk-position="right"
        :model="formValues"
        :rules="formRules"
        label-width="140px"
      >
        <div class="popTit">
          <svg-icon icon="mingcute:information-line" class="!w-[16px] ml-2" />提现会收取千分之一手续费
        </div>
        <el-form-item prop="" label="可入账银行卡" class="mt-4">
          <div class=" text-[#3194FA!important] cursor-pointer" @click="handleViewCard">
            查看
          </div>
        </el-form-item>
        <el-form-item prop="amount" label="提现金额">
          <el-input v-model="formValues.amount" autocomplete="new-password" :placeholder="`当前余额${data.availableBalance}元`" @keyup.enter="onConfirm">
            <template #suffix>
              元
            </template>
          </el-input>
        </el-form-item>
        <el-form-item prop="" label="" class="mt-4">
          <div class="text-[#BFBFBF]  text-sm">
            提现手续费率：1‰，提现手续费：{{ withdrawFee }}元,提现预计到账金额：  {{ withdrawAmount }}元
          </div>
        </el-form-item>
        <el-form-item prop="bankAccountId" label="">
          <template #label>
            <div class="flex items-center">
              提现到账账户
              <el-tooltip
                class="auth-tooltip"
                effect="light"
                content="提现账号只可选择已经报备通过的银行卡账户"
                placement="top"
              >
                <svg-icon class="!w-[16px] h-[16px] text-[#1777FF]" icon="mingcute:information-line" />
              </el-tooltip>
            </div>
          </template>
          <el-select
            v-model="formValues.bankAccountId"
            placeholder="请选择"
            clearable
            filterable
            @keyup.enter="onConfirm"
          >
            <el-option
              v-for="item in banks"
              :key="item.id"
              :label="item.bankAccount"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="提现银行名称">
          <el-input :model-value="bankName" disabled />
        </el-form-item>
        <el-form-item prop="payPassword" label="支付密码">
          <el-input v-model="formValues.payPassword" autocomplete="new-password" type="password" placeholder="请输入" @keyup.enter="onConfirm" />
        </el-form-item>
        <el-form-item label="支付手机号">
          <el-input :model-value="props.payPhone" disabled />
        </el-form-item>
        <el-form-item prop="code" class="relative" label="短信验证码">
          <el-input
            v-model="formValues.code"
            class="w-[280px] relative box-border pl36"
            clearable
            autocomplete="nony"
            placeholder="请输入"
            @keyup.enter="onConfirm"
          >
            <template #prefix>
              <svg-icon class="left-[10px] !w-[18px]" icon="lucide:shield-check" />
            </template>
          </el-input>

          <sms-button
            ref="smsBtnRef"
            class="relative h-[31px] !left-auto ml-[16px]"
            @click="doSendSms"
          />
        </el-form-item>
      </el-form>
    </div>

    <template #footer>
      <div class="text-right mr-8">
        <el-button @click="emit('close')">
          返回
        </el-button>
        <el-button type="primary" :loading="loading" @click="onConfirm">
          申请提现
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { checkNumber, checkSmsCode, scrollToErrorField } from '@core/tools';
import { ElMessage, FormInstance, FormRules } from 'element-plus';
import { nextTick, PropType, ref, watch, computed, toRaw } from 'vue';
import { SmsButton, SvgIcon } from '@vue3/components';
import { ESmsType, sendSmscode } from '@core/api';
import { queryAlipayUnitBankAccountList } from '@core/main/api';
import { IFinanceWallet, IAlipayUnitBankAccountListItem, IAlipayUnitWithdrawRequest } from '@core/main/types';
import { useRouter } from 'vue-router';

const router = useRouter();

let sendSmsLock = false;

const emit = defineEmits(['close', 'confirm']);

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  data: {
    type: Object as PropType<IFinanceWallet>,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  term: {
    type: String as PropType<'server' | 'company' | 'agent' | 'saas'>,
    default: 'server',
  },
  payPhone: {
    type: String,
    default: '',
  },
});

const withdrawFee = computed(() => {
  return Number((formValues.value.amount * 0.001).toFixed(2)) || 0;
});

const withdrawAmount = computed(() => {
  return Number((formValues.value.amount - withdrawFee.value).toFixed(2)) || 0;
});

const smsBtnRef = ref<InstanceType<typeof SmsButton>>(null);
const formRef = ref<FormInstance>();
const formValues = ref({} as IAlipayUnitWithdrawRequest);
const banks = ref<Array<IAlipayUnitBankAccountListItem>>([]);
const bankName = computed(() => {
  return banks.value.find((item) => item.id === formValues.value.bankAccountId)?.bankName;
});

const formRules = ref<FormRules<any>>({
  bankAccountId: [
    {
      required: true,

      message: '请选择提现账号',
    },
  ],
  amount: [
    {
      required: true,
      message: '请输入提现金额',
    },
    {
      validator: (rule: any, value: string, callback: any) => {
        if (checkNumber(String(formValues.value.amount), '提现金额', 2)) {
          callback(checkNumber(String(formValues.value.amount), '提现金额', 2));
        }
        callback();
      },
    },
  ],
  payPassword: [
    {
      required: true,
      message: '请输入提现密码',
    },
  ],
  code: [
    {
      required: true,
      message: '请输入验证码',
    },
    {
      required: true,
      validator: (rule: any, value: string, callback: any) => {
        if (checkSmsCode(String(formValues.value.code))) {
          callback(checkSmsCode(String(formValues.value.code)));
        }
        callback();
      },
    },
  ],
});

async function doSendSms() {
  if (sendSmsLock) return;

  if (!props.payPhone) {
    ElMessage.error('获取手机号失败');
    return;
  }

  try {
    sendSmsLock = true;
    smsBtnRef.value?.showLoading();

    await sendSmscode(ESmsType.SplitBill, props.payPhone);

    smsBtnRef.value?.countdown();
  } catch (error) {
    smsBtnRef.value?.hideLoading();
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }

  setTimeout(() => {
    sendSmsLock = false;
  }, 1300);
}

function onConfirm() {

  formRef.value?.validate(async (valid) => {
    if (valid) {
      if (formValues.value.amount > props.data.availableBalance) {
        ElMessage.error('提现金额大于余额');
        return;
      }
      const unitNo = banks.value.find((item) => item.id === formValues.value.bankAccountId)?.unitNo;

      emit('confirm', {
        ...toRaw(formValues.value),
        payPhone: props.payPhone,
        unitNo,
      } as IAlipayUnitWithdrawRequest);
    } else {
      nextTick(() => {
        scrollToErrorField(formRef.value?.$el, '.el-dialog__body');
      });
    }
  });
}

async function fetchBanks() {
  try {
    const { data } = await queryAlipayUnitBankAccountList({ serverId: props.data.serverId });

    banks.value = data;
  } catch (error) {
    // 不处理
  }
}
function handleViewCard() {
  router.push({
    name: 'FinanceIncomingAccount',
    query: { serverId: props.data.serverId },
  });
}

function resetForm() {
  for (const key in formValues.value) {
    formValues.value[key] = undefined;
  }
}

watch(
  () => props.visible,
  () => {
    fetchBanks();
  },
);

watch(
  () => props.visible,
  (val) => {
    if (val) {
      resetForm();
      nextTick(() => {
        formRef.value?.resetFields();
      });
    }
  },
);
</script>

<style lang="scss" scoped>

.popTit{
  text-indent: 4px;
  display: flex;
  align-items: center;
  width: 100%;
height: 28px;
line-height: 28px;
color: #FF4D4F;
background: #FFEDED;
border-radius: 2px 2px 2px 2px;
border: 1px solid #FFDBDA;
}
:deep(.el-select__wrapper) {
    height: 40px;
  }
:deep(.el-input__wrapper) {
    height: 40px;
  }
</style>
